import { useEffect, useRef, useState, useId } from "react";

const CONFIG = {
  locationId: "1RHfrgrrti3xMFR1LaOo",
  widgetId: "6988f3ff577219e3f254c5fc",
  voiceAiConfigId: "6982948d68720dcb7a15cc6b",
  apiEndpoint: "https://services.leadconnectorhq.com/chat-widget/public/start-voice-ai-call/",
  livekitUrl: "wss://retell-ai-4ihahnq7.livekit.cloud",
};

// One string for every way the call can fail to reach the service, so a
// visitor who cannot use the orb still leaves with the number in front of
// them rather than a browser error they cannot act on.
const VOICE_UNAVAILABLE =
  "The voice demo is unavailable right now. Call (813) 694-8671 to hear it.";

// A session that connects but never hears the agent ends here instead of
// leaving the visitor in silence (2026-10-03: a visitor's session lasted 0
// seconds, while the sessions before the 2026-09-28 GHL pause lasted 5 to 28
// seconds). The phone line runs on the same agent, so this points to booking.
const AGENT_JOIN_TIMEOUT_MS = 12000;
const AGENT_SILENT = "The voice agent did not answer. Please try again later or book a demo.";

interface LiveKitTrack {
  kind: unknown;
  attach(): HTMLElement;
}

interface LiveKitRoom {
  connect(url: string, token: string): Promise<void>;
  disconnect(): Promise<void> | void;
  localParticipant: {
    setMicrophoneEnabled(enabled: boolean): Promise<void>;
  };
  on(event: unknown, listener: (track: LiveKitTrack) => void): void;
  on(event: unknown, listener: () => void): void;
}

interface LiveKitClientApi {
  Room: new (options: Record<string, unknown>) => LiveKitRoom;
  RoomEvent: {
    TrackSubscribed: unknown;
    Disconnected: unknown;
  };
  Track: {
    Kind: {
      Audio: unknown;
    };
  };
}

type WindowWithLiveKit = Window & { LivekitClient?: LiveKitClientApi };

function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function generateMongoId() {
  const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, "0");
  const random = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  return timestamp + random;
}

function generateContactId() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length: 20 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

let liveKitPromise: Promise<void> | null = null;
function loadLiveKit() {
  if ((window as WindowWithLiveKit).LivekitClient) return Promise.resolve();
  if (liveKitPromise) return liveKitPromise;
  liveKitPromise = new Promise<void>((resolve, reject) => {
    const script = document.querySelector<HTMLScriptElement>('script[data-chleb-livekit]') ?? document.createElement('script');
    const timerRef: { current?: ReturnType<typeof setTimeout> } = {};
    const finish = (error?: Error) => {
      clearTimeout(timerRef.current);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      if (error) { script.remove(); liveKitPromise = null; reject(error); }
      else resolve();
    };
    const onLoad = () => finish((window as WindowWithLiveKit).LivekitClient ? undefined : new Error('Voice dependency unavailable'));
    const onError = () => finish(new Error('Voice dependency unavailable'));
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    timerRef.current = setTimeout(onError, 15000);
    if (!script.isConnected) {
      script.dataset.chlebLivekit = 'true';
      script.src = 'https://cdn.jsdelivr.net/npm/livekit-client@1.15.4/dist/livekit-client.umd.js';
      script.integrity = 'sha256-/xTb0c1fyOeDQJVME5n0WWbY1FgSH5rScAypt6MEyr8=';
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  });
  return liveKitPromise;
}
function untilAborted<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(new DOMException('Call canceled', 'AbortError'));
  return new Promise((resolve,reject) => {
    const cancel = () => { signal.removeEventListener('abort', cancel); reject(new DOMException('Call canceled', 'AbortError')); };
    signal.addEventListener('abort', cancel, { once: true });
    promise.then(value => { signal.removeEventListener('abort', cancel); resolve(value); }, error => { signal.removeEventListener('abort', cancel); reject(error); });
  });
}
export const VoiceAIOrb = () => {
  const roomRef = useRef<LiveKitRoom | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const attemptRef = useRef(0);
  const mounted = useRef(true);
  const audioElements = useRef<HTMLElement[]>([]);
  const heardRef = useRef(false);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusId = useId();
  const [state,setState] = useState<'idle'|'connecting'|'active'>('idle');
  const [message,setMessage] = useState('Ready when you are.');
  const release = () => {
    attemptRef.current += 1;
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    abortRef.current?.abort(); abortRef.current = null;
    const room = roomRef.current; roomRef.current = null;
    if (room) {
      // Disable capture even when the transport disconnect rejects.
      void room.localParticipant.setMicrophoneEnabled(false).catch(()=>undefined);
      try { void Promise.resolve(room.disconnect()).catch(()=>undefined); } catch { /* best-effort transport cleanup */ }
    }
    audioElements.current.forEach(el=>el.remove()); audioElements.current=[];
  };
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;release();}},[]);
  const endCall = () => {release();setState('idle');setMessage(state==='connecting'?'Connection canceled.':'Call ended.');};
  const startCall = async () => {
    if(abortRef.current || roomRef.current)return;
    const current = ++attemptRef.current;
    const controller = new AbortController(); abortRef.current=controller;
    heardRef.current=false;
    const valid=()=>mounted.current&&attemptRef.current===current&&!controller.signal.aborted;
    const ensure=()=>{if(!valid())throw new DOMException('Call canceled','AbortError');};
    const timer=setTimeout(()=>controller.abort(),45000);
    setState('connecting');setMessage('Allow microphone access to begin.');
    try {
      if(!navigator.mediaDevices?.getUserMedia)throw new Error('Microphone access is unavailable in this browser. Use the phone number instead.');
      // The permission stream is always stopped, including if permission resolves after cancellation.
      const permission=navigator.mediaDevices.getUserMedia({audio:true}).then(stream=>{stream.getTracks().forEach(track=>track.stop());});
      await untilAborted(permission,controller.signal); ensure();
      setMessage('Preparing the voice connection…');
      await untilAborted(loadLiveKit(),controller.signal); ensure();
      const sessionId=generateUUID();
      const payload={contactId:generateContactId(),callId:generateMongoId(),widgetId:CONFIG.widgetId,locationId:CONFIG.locationId,sessionId,sessionFingerprint:generateUUID(),eventData:{source:'direct',referrer:document.referrer||'',keyword:'',adSource:'',url_params:{},page:{url:window.location.href,title:document.title},timestamp:Date.now(),campaign:'',contactSessionIds:{ids:[sessionId]},type:'page-visit',pageVisitType:'text-widget',domain:window.location.hostname,version:'v3',parentId:'',parentName:'',fingerprint:null,documentURL:window.location.href}};
      // The voice service answers an error with no Access-Control-Allow-Origin
      // header, so the browser rejects the fetch outright instead of handing
      // back a non-ok response. The !response.ok branch below could therefore
      // never run on the failure it was written for, and the visitor read the
      // raw "Failed to fetch" (observed live 2026-09-20 against a persistent
      // HTTP 492 from the endpoint). Catch the transport rejection here and
      // say the same thing the branch below says.
      const response=await untilAborted(fetch(CONFIG.apiEndpoint+CONFIG.voiceAiConfigId,{method:'POST',headers:{'Content-Type':'application/json',Accept:'*/*'},body:JSON.stringify(payload),signal:controller.signal}).catch((cause:unknown)=>{
        if(controller.signal.aborted)throw cause;
        throw new Error(VOICE_UNAVAILABLE);
      }),controller.signal);
      ensure(); if(!response.ok)throw new Error(VOICE_UNAVAILABLE);
      const data=await untilAborted(response.json(),controller.signal) as {accessToken?:unknown};ensure();
      if(typeof data.accessToken!=='string'||!data.accessToken)throw new Error('The voice service did not confirm a connection. Please try again.');
      const LK=(window as WindowWithLiveKit).LivekitClient;if(!LK)throw new Error('Voice dependency unavailable');
      const room=new LK.Room({adaptiveStream:true,dynacast:true,audioCaptureDefaults:{autoGainControl:true,echoCancellation:true,noiseSuppression:true}});
      roomRef.current=room;
      room.on(LK.RoomEvent.TrackSubscribed,(track:LiveKitTrack)=>{if(valid()&&roomRef.current===room&&track.kind===LK.Track.Kind.Audio){heardRef.current=true;if(watchdogRef.current){clearTimeout(watchdogRef.current);watchdogRef.current=null;}const el=track.attach();el.setAttribute('data-voiceai-audio','chleb-orb');audioElements.current.push(el);document.body.appendChild(el);}});
      room.on(LK.RoomEvent.Disconnected,()=>{if(mounted.current&&roomRef.current===room){release();setState('idle');setMessage('Call disconnected. You can start again.');}});
      setMessage('Connecting…');
      const cleanupLate=()=>{if(!valid()){void room.localParticipant.setMicrophoneEnabled(false).catch(()=>undefined);try{void Promise.resolve(room.disconnect()).catch(()=>undefined);}catch{/* already closed */}}};
      await untilAborted(room.connect(CONFIG.livekitUrl,data.accessToken).then(cleanupLate),controller.signal);ensure();
      await untilAborted(room.localParticipant.setMicrophoneEnabled(true).then(cleanupLate),controller.signal);ensure();
      setState('active');setMessage('Connected. You can speak now.');
      if(!heardRef.current){watchdogRef.current=setTimeout(()=>{watchdogRef.current=null;if(mounted.current&&roomRef.current===room&&!heardRef.current){release();setState('idle');setMessage(AGENT_SILENT);}},AGENT_JOIN_TIMEOUT_MS);}
    } catch(error) {
      if(mounted.current&&attemptRef.current===current){
        const msg=error&&typeof error==='object'&&'name' in error&&error.name==='NotAllowedError'?'Microphone access was denied. Allow it in your browser settings, then try again.':controller.signal.aborted?'The connection timed out. Please try again or call us.':error instanceof Error?error.message:'Connection failed. Please try again.';
        release();setState('idle');setMessage(msg);
      }
    } finally {clearTimeout(timer);}
  };
  return <div className="voiceai-orb-wrapper rb-voice-control"><button data-voice-orb className={`voiceai-orb-btn ${state==='connecting'?'loading':''} ${state==='active'?'active':''}`} onClick={state==='idle'?startCall:endCall} aria-describedby={statusId} aria-label={state==='active'?'End call':state==='connecting'?'Cancel connection':'Start voice conversation'}><div className="spinner" aria-hidden="true"/><svg className="orb-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="8" y="3" width="8" height="12" rx="4"/><path d="M5 11v1a7 7 0 0 0 14 0v-1M12 19v3m-4 0h8"/></svg></button><p id={statusId} role="status" aria-live="polite" className="rb-voice-status">{message}</p>{state==='active'&&<span className="rb-call-connected">Call active · Tap to end</span>}{state==='connecting'&&<span className="rb-voice-hint">Tap again to cancel</span>}</div>;
};
